<template>
  <v-dialog
    v-model="dialog"
    width="700"
    persistent
  >
    <v-card rounded="xxl">
      <v-container>
        <v-row align="center">
          <v-col >
            <v-card-text>
              <v-row>
                <v-col>
                  <h1 style="color: green" class="mr-5">Staff Replacement <v-icon
              large
              style="color: green; font-size: 30px; width: 20px !important"
            >
              mdi-account-switch
            </v-icon>
          </h1>
                  <br />
                  <h3>Select replacement staff for leave approval</h3>
                  <br />
                  <v-alert
                    type="info"
                    dense
                    text
                    class="mb-3"
                  >
                    <strong>Leave Request:</strong> {{ leaveRequest.user?.first_name }} {{ leaveRequest.user?.last_name }}
                    <br>
                    <strong>Date:</strong> {{ leaveRequest.date }} | <strong>Time:</strong> {{ leaveRequest.start_time }} - {{ leaveRequest.end_time }}
                    <br>
                    <strong>Type:</strong> {{ formatLeaveType(leaveRequest.leave_type) }} | <strong>Reason:</strong> {{ leaveRequest.reason }}
                  </v-alert>
                </v-col>
              </v-row>

              <!-- Search Bar -->
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="search"
                    solo
                    rounded
                    append-icon="mdi-magnify"
                    label="Search Staff by name, code or phone"
                    hide-details
                    clearable
                    class="mb-4"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Staff List -->
              <v-row v-if="loading">
                <v-col cols="12" class="text-center py-5">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="50"
                  ></v-progress-circular>
                  <p class="mt-3">Loading available staff...</p>
                </v-col>
              </v-row>

              <v-row v-else-if="availableStaff.length === 0">
                <v-col cols="12">
                  <v-alert
                    type="warning"
                    prominent
                    border="left"
                  >
                    <v-row align="center">
                      <v-col class="grow">
                        <div class="title">No Available Staff</div>
                        <div>
                          No staff members are available for this timeslot.
                          You can still approve the leave without a replacement.
                        </div>
                      </v-col>
                    </v-row>
                  </v-alert>
                </v-col>
              </v-row>

              <v-row v-else>
                <v-col cols="12">
                  <div class="text-subtitle-2 mb-2">
                    Available Staff ({{ filteredStaff.length }})
                  </div>
                  <v-card
                    class="overflow-y-auto"
                    max-height="300"
                    elevation="0"
                    style="border: 1px solid #e0e0e0"
                  >
                    <v-list>
                      <v-list-item-group
                        v-model="selectedStaffId"
                        color="primary"
                      >
                        <v-list-item
                          v-for="staff in filteredStaff"
                          :key="staff.id"
                          :value="staff.id"
                          two-line
                        >
                          <v-list-item-avatar>
                            <v-img
                              v-if="staff.image_path"
                              :src="staff.image_path"
                            ></v-img>
                            <v-icon v-else color="grey lighten-1">mdi-account-circle</v-icon>
                          </v-list-item-avatar>

                          <v-list-item-content>
                            <v-list-item-title>
                              {{ staff.first_name }} {{ staff.middle_name }} {{ staff.last_name }}
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              Code: {{ staff.employee_code }} | Phone: {{ staff.phone }}
                            </v-list-item-subtitle>
                          </v-list-item-content>

                          <v-list-item-action>
                            <v-icon v-if="selectedStaffId === staff.id" color="primary">
                              mdi-checkbox-marked-circle
                            </v-icon>
                            <v-icon v-else color="grey lighten-1">
                              mdi-checkbox-blank-circle-outline
                            </v-icon>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Admin Remarks -->
              <v-row class="mt-4">
                <v-col cols="12">
                  <v-textarea
                    v-model="adminRemarks"
                    label="Admin Remarks (Optional)"
                    placeholder="Add any notes about this approval..."
                    rows="2"
                    solo
                    rounded
                    hide-details
                  ></v-textarea>
                </v-col>
              </v-row>

              <!-- Approve Without Replacement Option -->
              <v-row v-if="availableStaff.length === 0 || allowNoReplacement" class="mt-3">
                <v-col cols="12">
                  <v-checkbox
                    v-model="approveWithoutReplacement"
                    color="warning"
                    hide-details
                  >
                    <template v-slot:label>
                      <span class="warning--text">
                        <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
                        Approve without assigning replacement staff
                      </span>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>

              <!-- Action Buttons -->
              <v-row class="mt-5">
                <v-col cols="6">
                  <v-btn
                    color="grey"
                    block
                    rounded
                    @click="cancel"
                  >
                    Cancel
                  </v-btn>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    color="success"
                    block
                    rounded
                    :disabled="!canApprove"
                    :loading="approving"
                    @click="approveLeave"
                  >
                    <v-icon left>mdi-check</v-icon>
                    Approve Leave
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StaffReplacementModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    leaveRequest: {
      type: Object,
      required: true
    },
    allowNoReplacement: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false,
      loading: false,
      approving: false,
      availableStaff: [],
      selectedStaffId: null,
      search: '',
      adminRemarks: '',
      approveWithoutReplacement: false
    };
  },
  computed: {
    filteredStaff() {
      if (!this.search) return this.availableStaff;
      
      const searchLower = this.search.toLowerCase();
      return this.availableStaff.filter(staff => {
        const fullName = `${staff.first_name} ${staff.middle_name || ''} ${staff.last_name}`.toLowerCase();
        const code = staff.employee_code?.toLowerCase() || '';
        const phone = staff.phone?.toLowerCase() || '';
        
        return fullName.includes(searchLower) || 
               code.includes(searchLower) || 
               phone.includes(searchLower);
      });
    },
    canApprove() {
      return this.approveWithoutReplacement || this.selectedStaffId !== null;
    }
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.loadAvailableStaff();
      } else {
        this.resetForm();
      }
    },
    dialog(val) {
      this.$emit('input', val);
    }
  },
  methods: {
    formatLeaveType(type) {
      if (!type) return '-';
      return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    async loadAvailableStaff() {
      this.loading = true;
      this.availableStaff = [];
      this.selectedStaffId = null;
      
      try {
        // Encrypt the ID if encryption method exists
        const requestId = this.encryptIt ? this.encryptIt(this.leaveRequest.id) : this.leaveRequest.id;
        
        const response = await axios.get(
          `/leave_requests/${requestId}/available-staff`
        );
        
        if (response.data.success) {
          this.availableStaff = response.data.data || [];
          
          // If no staff available, auto-check approve without replacement
          if (this.availableStaff.length === 0) {
            this.approveWithoutReplacement = true;
          }
        } else {
          throw new Error(response.data.message || 'Failed to load available staff');
        }
      } catch (error) {
        console.error('Error loading available staff:', error);
        
        // Show user-friendly error message
        let errorMessage = 'Failed to load available staff. ';
        if (error.response?.status === 404) {
          errorMessage += 'Leave request not found.';
        } else if (error.response?.status === 401) {
          errorMessage += 'You are not authorized to perform this action.';
        } else {
          errorMessage += 'Please try again.';
        }
        
        this.$store.dispatch('showSnackbar', {
          text: errorMessage,
          color: 'error'
        });
        
        // If error, allow approval without replacement
        this.approveWithoutReplacement = true;
      } finally {
        this.loading = false;
      }
    },
    
    async approveLeave() {
      if (!this.canApprove) return;
      
      this.approving = true;
      try {
        // Encrypt the ID if encryption method exists
        const requestId = this.encryptIt ? this.encryptIt(this.leaveRequest.id) : this.leaveRequest.id;
        
        const payload = {
          replacement_user_id: this.approveWithoutReplacement ? null : this.selectedStaffId,
          admin_remarks: this.adminRemarks || null
        };
        
        const response = await axios.post(
          `/leave_requests/${requestId}/approve-with-replacement`,
          payload
        );
        
        if (response.data.success) {
          // Show success message
          this.$store.dispatch('showSnackbar', {
            text: response.data.message || 'Leave request approved successfully!',
            color: 'success'
          });
          
          // Emit success event with updated data
          this.$emit('approved', response.data.data);
          
          // Close dialog
          this.dialog = false;
        } else {
          throw new Error(response.data.message || 'Failed to approve leave request');
        }
      } catch (error) {
        console.error('Error approving leave:', error);
        
        // Show user-friendly error message
        let errorMessage = 'Failed to approve leave request. ';
        if (error.response?.status === 422) {
          // Validation error
          const errors = error.response.data.errors;
          if (errors) {
            errorMessage = Object.values(errors).flat().join(' ');
          }
        } else if (error.response?.status === 404) {
          errorMessage += 'Leave request not found.';
        } else if (error.response?.status === 401) {
          errorMessage += 'You are not authorized to perform this action.';
        } else {
          errorMessage += error.response?.data?.message || 'Please try again.';
        }
        
        this.$store.dispatch('showSnackbar', {
          text: errorMessage,
          color: 'error'
        });
      } finally {
        this.approving = false;
      }
    },
    
    cancel() {
      // Confirm cancellation if changes were made
      if (this.selectedStaffId || this.adminRemarks) {
        if (!confirm('Are you sure you want to cancel? Your changes will be lost.')) {
          return;
        }
      }
      
      this.dialog = false;
      this.$emit('cancelled');
    },
    
    resetForm() {
      this.selectedStaffId = null;
      this.adminRemarks = '';
      this.approveWithoutReplacement = false;
      this.search = '';
      this.availableStaff = [];
      this.loading = false;
      this.approving = false;
    }
  }
};
</script>

<style scoped>
.v-list-item--active {
  background-color: rgba(33, 150, 243, 0.1);
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>